import { observer } from "mobx-react";
import { Form, Modal, InputNumber, Divider, Checkbox } from "antd";
import { translateKey as t } from "../../../../../../locales/translation";
import { ClientsController } from "../../../ClientsController";
import { AppStore } from "~/stores/AppStore";
import { useEffect } from "react";
import { OFMultiSelect } from "../../../../../../components/OFMultiselect/OFMultiSelect";
import { OFDatePicker } from "../../../../../../components/OFDatePicker/OFDatePicker";

const DemoDataPostModal = observer(({ store, controller }: { store?: AppStore; controller?: ClientsController }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (controller.selectedDates && controller.selectedDates.length > 0) {
      form.setFields([
        {
          name: "dates",
          errors: [],
        },
      ]);
    }

    if (controller.depots.length === 1) {
      const depots = [controller.depots[0]._id];
      form.setFieldsValue({
        depotRefs: depots,
      });

      controller.setSelectedDepots(depots);
    }
  }, [controller, controller.depots, controller.selectedDates, form]);

  const handleOK = () => {
    form
      .validateFields()
      .then(() => {
        const formData = form.getFieldsValue();
        controller.processDemoData(formData);
        form.resetFields();
        controller.setSelectedDepots([]);
      })
      .catch(() => {});
  };

  const handleCancel = () => {
    store.clearClient();
    form.resetFields();
    controller.setDemoDataPostModal("visible", false);
    controller.setSelectedDepots([]);
  };

  const handleDepotSelection = (val, state, selectedAll) => {
    let currentList = controller.selectedDepots || [];

    if (state) {
      if (selectedAll) {
        currentList = controller.depots.map((dp) => dp._id);
      } else {
        currentList.push(val._id);
      }
    } else {
      if (selectedAll) {
        currentList = [];
      } else {
        currentList = currentList.filter((id) => id !== val._id);
      }
    }

    form.setFieldsValue({
      depotRefs: currentList,
    });
    controller.setSelectedDepots(currentList);
  };

  const renderForm = () => {
    const postTypes = [
      { label: "Routes", value: "routes" },
      { label: "Jobs", value: "jobs" },
    ];

    return (
      <div className="flex justify-center items-center demoDataPostModal">
        <Form
          requiredMark={false}
          form={form}
          className="w-full max-w-4xl"
          layout={"vertical"}
          initialValues={{
            radius: 10000,
            numRoutes: 10,
            numStops: 10,
            numJobsPerStop: 1,
            numItemsPerJob: 2,
            numStopsWithClusterRef: 2,
            numJobs: 10,
            geocode: false,
            dates: [],
            depotRefs: [],
            postTypes: ["routes"],
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item className="w-full" label="Depot" name="depotRefs" rules={[{ required: true, message: "At least 1 Depot is required" }]} required tooltip="This is a required field">
              <OFMultiSelect
                data={controller.depots}
                renderItem={(item: any) => item.name}
                placeholder={controller.selectedDepots.length > 0 ? `${controller.selectedDepots.length} selected` : "Select"}
                selectedItems={controller.selectedDepots}
                style={{ width: "100%" }}
                menuWidth={200}
                onSelect={handleDepotSelection}
                showSearch={true}
                canSelectAll={true}
              />
            </Form.Item>

            <Form.Item
              className="w-full"
              label="Date"
              name="dates"
              rules={[
                {
                  required: true,
                  message: "At least 1 Date is required",
                  validator: (_, value) => {
                    if (controller.selectedDates && controller.selectedDates.length > 0) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("At least 1 Date is required"));
                  },
                },
              ]}
              required
              tooltip="This is a required field"
            >
              <OFDatePicker
                controller={controller}
                selectedDates={controller.selectedDates}
                placeholder={controller.selectedDates.length > 0 ? `${controller.selectedDates.length} selected` : "Select"}
                allowClear
              />
            </Form.Item>
          </div>

          <Form.Item className="w-full" label="Post Type" name="postTypes">
            <Checkbox.Group options={postTypes} defaultValue={["routes"]} onChange={(e) => (controller.postTypes = e)} />
          </Form.Item>

          <Divider orientation="left" orientationMargin="0">
            Specifications
          </Divider>

          <div className={controller.postTypes.includes("routes") ? "block" : "hidden"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item className="w-full" label="No of Routes" name="numRoutes">
                <InputNumber type="number" className="w-full" step={1} max={15} min={1} precision={0} parser={(value) => parseInt(value.replace(/[^\d.-]/g, "0"))} />
              </Form.Item>

              <Form.Item className="w-full" label="No of Stops" name="numStops">
                <InputNumber type="number" className="w-full" step={1} max={15} min={1} precision={0} parser={(value) => parseInt(value.replace(/[^\d.-]/g, "0"))} />
              </Form.Item>

              <Form.Item className="w-full" label="No of Jobs Per Stop" name="numJobsPerStop">
                <InputNumber type="number" className="w-full" step={1} max={5} min={1} precision={0} parser={(value) => parseInt(value.replace(/[^\d.-]/g, "0"))} />
              </Form.Item>

              <Form.Item className="w-full" label="No of Items Per Job" name="numItemsPerJob">
                <InputNumber type="number" className="w-full" step={1} max={5} min={1} precision={0} parser={(value) => parseInt(value.replace(/[^\d.-]/g, "0"))} />
              </Form.Item>

              {/* <Form.Item className="w-full" label="No of Stops With ClusterRef" name="numStopsWithClusterRef">
                <InputNumber type="number" className="w-full" step={1} max={5} min={1} precision={0} parser={(value) => parseInt(value.replace(/[^\d.-]/g, "0"))} />
              </Form.Item> */}
            </div>
          </div>

          <div className={controller.postTypes.includes("jobs") ? "block" : "hidden"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item className="w-full" label="No of Jobs" name="numJobs">
                <InputNumber type="number" className="w-full" step={1} max={15} min={1} precision={0} parser={(value) => parseInt(value.replace(/[^\d.-]/g, "0"))} />
              </Form.Item>
            </div>
          </div>

          <Divider orientation="left" orientationMargin="0">
            Location
          </Divider>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item className="w-full" label="Radius" name="radius">
              <InputNumber type="number" className="w-full" step={1} min={5000} max={15000} precision={0} parser={(value) => parseInt(value.replace(/[^\d.-]/g, "0"))} />
            </Form.Item>

            {/* <Form.Item className="w-full" name="geocode" valuePropName="checked" >
              <Checkbox>Geocode</Checkbox>
            </Form.Item> */}
          </div>
        </Form>
      </div>
    );
  };

  return (
    <Modal
      title="Demo Data Post"
      styles={{
        header: { borderBottom: "1px solid #0505050f", paddingBottom: 10 },
      }}
      open={controller.demoDataPostModal.visible}
      cancelText={t("lblCancel", "Cancel")}
      okText={t("lblSubmit", "Submit")}
      onCancel={handleCancel}
      centered={true}
      okType="primary"
      onOk={handleOK}
      maskClosable={false}
      okButtonProps={{ disabled: controller.postTypes.length === 0 }}
    >
      {renderForm()}
    </Modal>
  );
});

export default DemoDataPostModal;
